export const DEFAULT_PUBLIC_PATHS = ['/login', '/register', '/forgot-password'];
export const DEFAULT_PUBLIC_API_PATTERNS = ['/api/health', '/api/public'];

export type AuthRecoveryAction = 'ignore' | 'retry' | 'soft_prompt' | 'redirect';

export interface AuthRecoveryInput {
  status: number;
  retryCount: number;
  pathname: string;
  requestUrl: string;
  consecutive401Failures: number;
  now: number;
  lastRedirectTime: number;
  maxConsecutiveFailures: number;
  redirectCooldownMs: number;
  publicPaths?: string[];
  publicApiPatterns?: string[];
}

export interface AuthRecoveryDecision {
  action: AuthRecoveryAction;
  nextConsecutive401Failures: number;
  nextLastRedirectTime: number;
}

export function shouldSkipAuthRedirect(
  pathname: string,
  requestUrl: string,
  publicPaths: string[] = DEFAULT_PUBLIC_PATHS,
  publicApiPatterns: string[] = DEFAULT_PUBLIC_API_PATTERNS
): boolean {
  if (publicPaths.some((path) => pathname.startsWith(path))) {
    return true;
  }

  return publicApiPatterns.some((pattern) => requestUrl.includes(pattern));
}

export function evaluateAuthRecovery(input: AuthRecoveryInput): AuthRecoveryDecision {
  const {
    status,
    retryCount,
    pathname,
    requestUrl,
    consecutive401Failures,
    now,
    lastRedirectTime,
    maxConsecutiveFailures,
    redirectCooldownMs,
    publicPaths = DEFAULT_PUBLIC_PATHS,
    publicApiPatterns = DEFAULT_PUBLIC_API_PATTERNS,
  } = input;

  if (status !== 401) {
    return {
      action: 'ignore',
      nextConsecutive401Failures: consecutive401Failures,
      nextLastRedirectTime: lastRedirectTime,
    };
  }

  if (shouldSkipAuthRedirect(pathname, requestUrl, publicPaths, publicApiPatterns)) {
    return {
      action: 'ignore',
      nextConsecutive401Failures: consecutive401Failures,
      nextLastRedirectTime: lastRedirectTime,
    };
  }

  if (retryCount < 1) {
    return {
      action: 'retry',
      nextConsecutive401Failures: consecutive401Failures,
      nextLastRedirectTime: lastRedirectTime,
    };
  }

  const nextFailures = consecutive401Failures + 1;
  const canRedirect =
    nextFailures >= maxConsecutiveFailures &&
    now - lastRedirectTime >= redirectCooldownMs;

  return {
    action: canRedirect ? 'redirect' : 'soft_prompt',
    nextConsecutive401Failures: nextFailures,
    nextLastRedirectTime: canRedirect ? now : lastRedirectTime,
  };
}
